// Módulo Marketing — gestão de posts pra redes sociais.
// Sprint M.1: workflow rascunho → em_revisao → aprovado/rejeitado → agendado → publicado.
// Inspirado em Buffer/Later/Hootsuite. Backend: actions mkt_* em POST /atividades.
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  Card, Button, Input, Select, Tag, Modal, Form, Typography, Space,
  Radio, App as AntApp, Flex, Empty, DatePicker, Checkbox, Spin, Image,
  Segmented, Mentions, Drawer, Alert, Tabs, Progress,
} from 'antd';
import {
  PlusOutlined, SearchOutlined, DeleteOutlined, CheckCircleOutlined,
  CloseCircleOutlined, SendOutlined, ClockCircleOutlined, PictureOutlined,
  LeftOutlined, RightOutlined, CalendarOutlined, AppstoreOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';  // (kept for future list view)
import dayjs from 'dayjs';
import { API_URL } from '../config';
import { hojeIso } from '../utils/dataFmt';
import { usePerms } from '../hooks/usePerms';

const { Title, Text } = Typography;

async function getToken(): Promise<string> {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString() ?? '';
}

// ─── Tipos ──────────────────────────────────────────────────────────────
interface Midia {
  s3_key: string;
  url_publica: string;
  tipo: 'imagem' | 'video' | 'documento';
  mime: string;
  tamanho_bytes: number;
  ordem?: number;
  nome_original?: string | null;
}
interface MidiaUpload {
  data: string;   // data URL base64
  nome: string;
  mime: string;
}
interface Post {
  id: string;
  titulo: string;
  conteudo: string | null;
  redes: string[];
  midias: Midia[];
  hashtags: string | null;
  status: 'rascunho' | 'em_revisao' | 'aprovado' | 'agendado' | 'publicado' | 'rejeitado' | 'arquivado';
  publicar_em: string | null;
  publicado_em: string | null;
  criado_por: string;
  aprovador_email: string | null;
  aprovado_por: string | null;
  aprovado_em: string | null;
  rejeitado_motivo: string | null;
  origem: string;
  meta: any;
  tags: string[] | null;
  criado_em: string;
  atualizado_em: string;
  n_comentarios?: number;
}

const STATUS: Record<string, { label: string; cor: string; bg: string }> = {
  rascunho:    { label: 'Rascunho',    cor: '#6A7092', bg: '#F0F1F5' },
  em_revisao:  { label: 'Em revisão',  cor: '#B07500', bg: '#FFF8E8' },
  aprovado:    { label: 'Aprovado',    cor: '#0a7f4a', bg: '#E8F5EE' },
  agendado:    { label: 'Agendado',    cor: '#0930D0', bg: '#EFF3FF' },
  publicado:   { label: 'Publicado',   cor: '#7C3AED', bg: '#F1ECFD' },
  rejeitado:   { label: 'Rejeitado',   cor: '#B91626', bg: '#FDECEC' },
  arquivado:   { label: 'Arquivado',   cor: '#888',    bg: '#F2F2F4' },
};
const COLUNAS = ['rascunho', 'em_revisao', 'aprovado', 'agendado', 'publicado', 'rejeitado'];

function iconePorMime(mime: string): string {
  if (!mime) return '📄';
  if (mime === 'application/pdf') return '📕';
  if (mime.includes('word') || mime === 'application/msword') return '📘';
  if (mime.includes('sheet') || mime === 'application/vnd.ms-excel' || mime === 'text/csv') return '📗';
  if (mime.includes('presentation') || mime === 'application/vnd.ms-powerpoint') return '📙';
  if (mime.startsWith('text/')) return '📃';
  return '📄';
}

const REDES: Record<string, { label: string; emoji: string; cor: string }> = {
  instagram: { label: 'Instagram', emoji: '📷', cor: '#E1306C' },
  linkedin:  { label: 'LinkedIn',  emoji: '💼', cor: '#0077B5' },
  facebook:  { label: 'Facebook',  emoji: '👥', cor: '#1877F2' },
  tiktok:    { label: 'TikTok',    emoji: '🎵', cor: '#000' },
  x:         { label: 'X',          emoji: '𝕏',  cor: '#000' },
  youtube:   { label: 'YouTube',   emoji: '▶️', cor: '#FF0000' },
};

export default function Marketing() {
  return <AntApp><MarketingInner /></AntApp>;
}

function MarketingInner() {
  const { message } = AntApp.useApp();
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [usuarios, setUsuarios] = useState<{ email: string; nome: string }[]>([]);
  const [filtroStatus, setFiltroStatus] = useState<string[]>(() => {
    const raw = sessionStorage.getItem('mkt_filtro_status');
    if (raw) { try { return JSON.parse(raw); } catch {} }
    return ['rascunho', 'em_revisao', 'aprovado', 'agendado'];
  });
  useEffect(() => {
    sessionStorage.setItem('mkt_filtro_status', JSON.stringify(filtroStatus));
  }, [filtroStatus]);
  const [busca, setBusca] = useState('');
  const [editando, setEditando] = useState<Post | null>(null);
  const [criando, setCriando] = useState(false);
  const [novoComData, setNovoComData] = useState<string | null>(null);
  const [meuEmail, setMeuEmail] = useState('');
  // Onda F5: ehAdmin via matriz nova (admin.permissoes.editar) em vez de
  // Cognito group "admin".
  const { pode } = usePerms();
  const ehAdmin = pode('admin.permissoes.editar');
  const [visao, setVisao] = useState<'kanban' | 'calendario'>(() => {
    const raw = localStorage.getItem('mkt_visao');
    return raw === 'calendario' ? 'calendario' : 'kanban';
  });
  useEffect(() => { localStorage.setItem('mkt_visao', visao); }, [visao]);

  useEffect(() => {
    (async () => {
      const session = await fetchAuthSession();
      const payload: any = session.tokens?.idToken?.payload || {};
      setMeuEmail((payload.email || '').toLowerCase());
      // F5: ehAdmin agora vem do hook usePerms() (matriz). Cognito groups
      // continua disponivel no JWT mas nao usado mais aqui.
      try {
        const token = await getToken();
        const r = await axios.get(`${API_URL}/admin/usuarios?modulo=marketing`, { headers: { Authorization: token } });
        const data = r.data.body ? JSON.parse(r.data.body) : r.data;
        setUsuarios((Array.isArray(data) ? data : []).filter((u: any) => u.enabled !== false)
          .map((u: any) => ({ email: u.email, nome: u.nome || u.email })));
      } catch { setUsuarios([]); }
    })();
  }, []);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/atividades`,
        { action: 'mkt_post_listar',
          status: filtroStatus.length > 0 ? filtroStatus : undefined,
          busca: busca || undefined,
        },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setPosts(Array.isArray(d) ? d : []);
    } catch (e: any) {
      message.error('Erro ao carregar posts: ' + (e.response?.data?.erro || e.message));
    } finally { setLoading(false); }
  }, [filtroStatus, busca, message]);

  useEffect(() => { carregar(); }, [carregar]);

  // Deep-link: abre post automaticamente quando vem do sino ou da lista de Atividades
  useEffect(() => {
    const abrirId = sessionStorage.getItem('mkt_posts_abrir_id');
    if (!abrirId) return;
    sessionStorage.removeItem('mkt_posts_abrir_id');
    (async () => {
      try {
        const token = await getToken();
        const r = await axios.post(`${API_URL}/atividades`,
          { action: 'mkt_post_obter', id: abrirId },
          { headers: { Authorization: token } });
        const d = r.data.body ? JSON.parse(r.data.body) : r.data;
        if (d && d.id) setEditando(d as Post);
      } catch { /* silencioso — post pode ter sido excluído */ }
    })();
  }, []);

  return (
    <div style={{ maxWidth: 1500, margin: '16px auto', padding: '0 24px' }}>
      <Flex justify="space-between" align="center" wrap="wrap" gap={10} style={{ marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0, color: '#020D7F' }}>
          📱 Marketing — Posts
        </Title>
        <Space wrap>
          <Segmented
            size="small"
            value={visao}
            onChange={(v) => setVisao(v as 'kanban' | 'calendario')}
            options={[
              { label: 'Kanban', value: 'kanban', icon: <AppstoreOutlined /> },
              { label: 'Calendário', value: 'calendario', icon: <CalendarOutlined /> },
            ]}
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCriando(true)}>
            Novo post
          </Button>
        </Space>
      </Flex>

      <Card size="small" style={{ background: '#FAFBFF', marginBottom: 14 }} bodyStyle={{ padding: 10 }}>
        <Flex gap={12} align="center" wrap="wrap">
          <Input value={busca} onChange={e => setBusca(e.target.value)}
            placeholder="Buscar título / conteúdo…"
            prefix={<SearchOutlined style={{ color: '#aaa' }} />}
            size="small" allowClear style={{ minWidth: 240, flex: 1 }} />

          <Space size={4}>
            <Text type="secondary" style={{ fontSize: 11 }}>Status:</Text>
            <Select
              mode="multiple" value={filtroStatus} onChange={setFiltroStatus}
              placeholder="Todos" allowClear size="small"
              style={{ minWidth: 220, maxWidth: 400 }}
              maxTagCount="responsive"
              options={Object.entries(STATUS).map(([k, v]) => ({ label: v.label, value: k }))} />
          </Space>

          <Text type="secondary" style={{ fontSize: 11, marginLeft: 'auto' }}>
            {posts.length} post(s)
          </Text>
        </Flex>
      </Card>

      {visao === 'kanban' ? (
        <KanbanPosts posts={posts} loading={loading}
          meuEmail={meuEmail}
          onEditar={setEditando}
          onMudou={carregar} />
      ) : (
        <CalendarioPosts posts={posts} loading={loading}
          onEditar={setEditando}
          onCriarComData={(iso) => { setNovoComData(iso); setCriando(true); }}
          onMudou={carregar} />
      )}

      {(editando || criando) && (
        <ModalPost
          post={editando}
          publicarEmInicial={novoComData}
          meuEmail={meuEmail}
          ehAdmin={ehAdmin}
          usuarios={usuarios}
          onClose={() => { setEditando(null); setCriando(false); setNovoComData(null); }}
          onSalvo={() => { setEditando(null); setCriando(false); setNovoComData(null); carregar(); }}
        />
      )}
    </div>
  );
}

// ─── Kanban ─────────────────────────────────────────────────────────────
function KanbanPosts({ posts, loading, meuEmail, onEditar, onMudou }: {
  posts: Post[]; loading: boolean; meuEmail: string;
  onEditar: (p: Post) => void; onMudou: () => void;
}) {
  const { message } = AntApp.useApp();
  const [arrastando, setArrastando] = useState<string | null>(null);
  const [hoverCol, setHoverCol] = useState<string | null>(null);
  const [colapsadas, setColapsadas] = useState<Set<string>>(() => {
    const raw = localStorage.getItem('mkt_colunas_colapsadas');
    if (raw) { try { return new Set(JSON.parse(raw)); } catch {} }
    return new Set(['publicado', 'rejeitado']);
  });
  function toggleColapsada(s: string) {
    setColapsadas(prev => {
      const n = new Set(prev);
      if (n.has(s)) n.delete(s); else n.add(s);
      localStorage.setItem('mkt_colunas_colapsadas', JSON.stringify(Array.from(n)));
      return n;
    });
  }

  async function moverPara(post: Post, novoStatus: string) {
    // 'rejeitado' precisa de motivo — pra drag-drop, pula (usuário usa botão dedicado)
    if (novoStatus === 'rejeitado') {
      message.info('Pra rejeitar, abra o post e clique em "Rejeitar" — precisa informar motivo.');
      return;
    }
    // 'agendado' precisa de publicar_em
    if (novoStatus === 'agendado' && !post.publicar_em) {
      message.warning('Defina uma data de publicação antes de agendar.');
      return;
    }
    try {
      const token = await getToken();
      // Mapeia status pra action específica (workflow-aware). 'rascunho' não tem
      // action própria — usa update genérico com status no payload.
      const actionMap: Record<string, string> = {
        em_revisao: 'mkt_post_enviar_revisao',
        aprovado: 'mkt_post_aprovar',
        agendado: 'mkt_post_agendar',
        publicado: 'mkt_post_publicar',
        arquivado: 'mkt_post_arquivar',
      };
      const action = actionMap[novoStatus] || 'mkt_post_atualizar';
      const payload: any = { action, id: post.id };
      if (action === 'mkt_post_atualizar') payload.status = novoStatus;
      if (post.publicar_em) payload.publicar_em = post.publicar_em;
      const r = await axios.post(`${API_URL}/atividades`, payload,
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      if (d?.erro) {
        message.error(d.erro);
        return;
      }
      message.success(`Movido pra "${STATUS[novoStatus]?.label || novoStatus}"`);
      onMudou();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.erro || e.message));
    }
  }

  if (loading) return <Card loading />;

  const gridCols = COLUNAS.map(s => colapsadas.has(s) ? '40px' : 'minmax(0, 1fr)').join(' ');

  return (
    <div style={{ display: 'grid', gridTemplateColumns: gridCols, gap: 12, alignItems: 'flex-start' }}>
      {COLUNAS.map(status => {
        const itens = posts.filter(p => p.status === status);
        const st = STATUS[status];
        const isHover = hoverCol === status;
        const isColapsada = colapsadas.has(status);

        if (isColapsada) {
          return (
            <div key={status}
              onDragOver={e => { e.preventDefault(); setHoverCol(status); }}
              onDragLeave={() => setHoverCol(null)}
              onDrop={e => {
                e.preventDefault();
                const id = e.dataTransfer.getData('text/post-id');
                const p = posts.find(x => x.id === id);
                if (p && p.status !== status) moverPara(p, status);
                setArrastando(null); setHoverCol(null);
              }}
              onClick={() => toggleColapsada(status)}
              title={`Expandir ${st.label} (${itens.length})`}
              style={{
                cursor: 'pointer', minHeight: 300,
                background: isHover ? st.bg : '#FAFBFF',
                border: `1px solid ${isHover ? st.cor : '#e0e0e0'}`,
                borderRadius: 6,
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                padding: '10px 4px',
              }}>
              <Text style={{ fontSize: 14, color: st.cor }}>›</Text>
              <div style={{ marginTop: 8, writingMode: 'vertical-rl',
                transform: 'rotate(180deg)', color: st.cor,
                fontWeight: 700, fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase' }}>
                {st.label} ({itens.length})
              </div>
            </div>
          );
        }

        return (
          <div key={status}
            onDragOver={e => { e.preventDefault(); setHoverCol(status); }}
            onDragLeave={() => setHoverCol(null)}
            onDrop={e => {
              e.preventDefault();
              const id = e.dataTransfer.getData('text/post-id');
              const p = posts.find(x => x.id === id);
              if (p && p.status !== status) moverPara(p, status);
              setArrastando(null); setHoverCol(null);
            }}>
            <Card size="small" style={{
              minHeight: 300,
              background: isHover ? st.bg : undefined,
              borderColor: isHover ? st.cor : undefined,
            }}
              title={
                <Flex justify="space-between" align="center" style={{ minWidth: 0 }}>
                  <Text strong ellipsis style={{ color: st.cor, textTransform: 'uppercase', letterSpacing: 1, fontSize: 11, minWidth: 0 }}>
                    {st.label} ({itens.length})
                  </Text>
                  <Button size="small" type="text"
                    onClick={() => toggleColapsada(status)}
                    style={{ fontSize: 12, color: st.cor, padding: '0 4px', height: 20 }}>‹</Button>
                </Flex>
              }
              styles={{ header: { borderBottom: `2px solid ${st.cor}`, padding: '6px 12px', minHeight: 0 } }}>
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                {itens.length === 0 && (
                  <Text type="secondary" italic style={{ fontSize: 10, display: 'block',
                    textAlign: 'center', padding: '20px 4px' }}>
                    Solte aqui
                  </Text>
                )}
                {itens.map(p => (
                  <CardPost key={p.id} post={p}
                    arrastando={arrastando === p.id}
                    meuEmail={meuEmail}
                    onDragStart={() => setArrastando(p.id)}
                    onClick={() => onEditar(p)} />
                ))}
              </Space>
            </Card>
          </div>
        );
      })}
    </div>
  );
}

// ─── Card de post no Kanban ─────────────────────────────────────────────
function CardPost({ post, arrastando, meuEmail, onDragStart, onClick }: {
  post: Post; arrastando: boolean; meuEmail: string;
  onDragStart: () => void; onClick: () => void;
}) {
  const capa = (post.midias || [])[0];
  const ehAprovador = post.aprovador_email?.toLowerCase() === meuEmail.toLowerCase();
  const dias = post.publicar_em
    ? Math.floor((new Date(post.publicar_em).getTime() - Date.now()) / 86400000)
    : null;

  return (
    <Card key={post.id} size="small" hoverable draggable
      onDragStart={e => { e.dataTransfer.setData('text/post-id', post.id); onDragStart(); }}
      onClick={onClick}
      style={{
        opacity: arrastando ? 0.6 : 1,
        boxShadow: arrastando ? '0 4px 12px rgba(0,0,0,0.15)' : undefined,
        borderLeft: post.status === 'em_revisao' && ehAprovador ? '3px solid #B07500' : undefined,
      }}
      bodyStyle={{ padding: 0 }}
      cover={capa?.tipo === 'imagem' ? (
        <div style={{ height: 100, overflow: 'hidden', borderRadius: '4px 4px 0 0' }}>
          <img src={capa.url_publica} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </div>
      ) : capa?.tipo === 'video' ? (
        <div style={{ height: 100, background: '#000', display: 'flex',
          alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 30 }}>▶</div>
      ) : undefined}>
      <div style={{ padding: 8 }}>
        <Text strong style={{ fontSize: 12, color: '#020D7F', display: 'block' }}>
          {post.titulo}
        </Text>
        {post.conteudo && (
          <Text type="secondary" style={{ fontSize: 10, display: '-webkit-box',
            WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            marginTop: 2 }}>
            {post.conteudo}
          </Text>
        )}
        <Flex gap={3} wrap style={{ marginTop: 4 }}>
          {(post.redes || []).map(r => (
            <span key={r} title={REDES[r]?.label || r} style={{ fontSize: 11 }}>
              {REDES[r]?.emoji || '🔗'}
            </span>
          ))}
        </Flex>
        <Flex justify="space-between" align="center" style={{ marginTop: 6 }}>
          <Text type="secondary" style={{ fontSize: 9 }}>
            {post.criado_por.split('@')[0]}
          </Text>
          {dias != null && (
            <Text style={{ fontSize: 9,
              color: dias < 0 ? '#B91626' : dias === 0 ? '#B07500' : '#0930D0' }}>
              {dias < 0 ? `há ${Math.abs(dias)}d` : dias === 0 ? 'hoje' : `em ${dias}d`}
            </Text>
          )}
          {(post.n_comentarios || 0) > 0 && (
            <Text type="secondary" style={{ fontSize: 9 }}>💬 {post.n_comentarios}</Text>
          )}
        </Flex>
      </div>
    </Card>
  );
}

// ─── Calendário visual mensal ───────────────────────────────────────────
const MESES_PT = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const DIAS_SEM = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];

function CalendarioPosts({ posts, loading, onEditar, onCriarComData, onMudou }: {
  posts: Post[]; loading: boolean;
  onEditar: (p: Post) => void;
  onCriarComData: (publicarEm: string) => void;
  onMudou: () => void;
}) {
  const [mesRef, setMesRef] = useState<dayjs.Dayjs>(() => {
    const raw = sessionStorage.getItem('mkt_calendario_mes');
    if (raw) {
      const d = dayjs(raw);
      if (d.isValid()) return d.startOf('month');
    }
    return dayjs().startOf('month');
  });
  useEffect(() => {
    sessionStorage.setItem('mkt_calendario_mes', mesRef.toISOString());
  }, [mesRef]);

  const [arrastando, setArrastando] = useState<string | null>(null);
  const [hoverDia, setHoverDia] = useState<string | null>(null);

  async function moverParaData(post: Post, novaData: dayjs.Dayjs) {
    try {
      const token = await getToken();
      const base = post.publicar_em ? dayjs(post.publicar_em) : dayjs().hour(9).minute(0);
      const novo = novaData.hour(base.hour()).minute(base.minute()).second(0);
      // Não faz nada se a data não mudou
      if (post.publicar_em && dayjs(post.publicar_em).isSame(novo, 'day')) return;
      await axios.post(`${API_URL}/atividades`,
        { action: 'mkt_post_atualizar', id: post.id, publicar_em: novo.toISOString() },
        { headers: { Authorization: token } });
      onMudou();
    } catch (e: any) {
      alert('Erro ao reagendar: ' + (e.response?.data?.erro || e.message));
    }
  }

  if (loading) return <Card loading />;

  // Calcula range do grid: Seg da semana do dia 1 → Dom da semana do último dia
  const primeiroDoMes = mesRef;
  const offsetInicio = (primeiroDoMes.day() + 6) % 7;  // 0=Seg, 6=Dom
  const inicioGrid = primeiroDoMes.subtract(offsetInicio, 'day');
  const ultimoDoMes = mesRef.endOf('month').startOf('day');
  const offsetFim = 6 - ((ultimoDoMes.day() + 6) % 7);
  const fimGrid = ultimoDoMes.add(offsetFim, 'day');

  const dias: dayjs.Dayjs[] = [];
  let cursor = inicioGrid;
  while (cursor.isBefore(fimGrid) || cursor.isSame(fimGrid, 'day')) {
    dias.push(cursor);
    cursor = cursor.add(1, 'day');
  }
  const semanas: dayjs.Dayjs[][] = [];
  for (let i = 0; i < dias.length; i += 7) semanas.push(dias.slice(i, i + 7));

  // Agrupa posts com data por dia (YYYY-MM-DD), ordenados por hora
  const postsPorDia: Record<string, Post[]> = {};
  for (const p of posts) {
    if (!p.publicar_em) continue;
    const k = dayjs(p.publicar_em).format('YYYY-MM-DD');
    if (!postsPorDia[k]) postsPorDia[k] = [];
    postsPorDia[k].push(p);
  }
  for (const k of Object.keys(postsPorDia)) {
    postsPorDia[k].sort((a, b) =>
      dayjs(a.publicar_em!).valueOf() - dayjs(b.publicar_em!).valueOf());
  }
  const postsSemData = posts.filter(p => !p.publicar_em).length;
  const hoje = dayjs();

  return (
    <div>
      {/* Barra de navegação do mês */}
      <Card size="small" style={{ marginBottom: 12 }} bodyStyle={{ padding: 10 }}>
        <Flex justify="space-between" align="center" wrap gap={8}>
          <Space>
            <Button icon={<LeftOutlined />} size="small"
              onClick={() => setMesRef(mesRef.subtract(1, 'month'))}
              title="Mês anterior" />
            <Text strong style={{ minWidth: 170, textAlign: 'center', display: 'inline-block',
              color: '#020D7F', fontSize: 14 }}>
              {MESES_PT[mesRef.month()]} de {mesRef.year()}
            </Text>
            <Button icon={<RightOutlined />} size="small"
              onClick={() => setMesRef(mesRef.add(1, 'month'))}
              title="Próximo mês" />
            <Button size="small" onClick={() => setMesRef(dayjs().startOf('month'))}>
              Hoje
            </Button>
          </Space>
          {postsSemData > 0 && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              ⚠ {postsSemData} post(s) sem data — visíveis no Kanban
            </Text>
          )}
        </Flex>
      </Card>

      {/* Grid responsivo: rola horizontal em viewport estreita */}
      <div style={{ overflowX: 'auto' }}>
        <div style={{ minWidth: 560 }}>
          {/* Cabeçalho dos dias da semana */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(80px, 1fr))',
            gap: 4, marginBottom: 4 }}>
            {DIAS_SEM.map(d => (
              <div key={d} style={{ textAlign: 'center', fontSize: 11, fontWeight: 600,
                color: '#6A7092', textTransform: 'uppercase', letterSpacing: 1, padding: '4px 0' }}>
                {d}
              </div>
            ))}
          </div>

          {/* Semanas */}
          <div style={{ display: 'grid', gap: 4 }}>
            {semanas.map((semana, si) => (
              <div key={si} style={{ display: 'grid',
                gridTemplateColumns: 'repeat(7, minmax(80px, 1fr))', gap: 4 }}>
                {semana.map(dia => {
                  const k = dia.format('YYYY-MM-DD');
                  const itens = postsPorDia[k] || [];
                  const ehHoje = dia.isSame(hoje, 'day');
                  const ehMesAtual = dia.month() === mesRef.month();
                  const ehFimSemana = dia.day() === 0 || dia.day() === 6;
                  const isHover = hoverDia === k;
                  const visiveis = itens.slice(0, 3);
                  const restante = itens.length - visiveis.length;

                  return (
                    <div key={k}
                      onDragOver={e => { e.preventDefault(); setHoverDia(k); }}
                      onDragLeave={() => setHoverDia(null)}
                      onDrop={e => {
                        e.preventDefault();
                        const id = e.dataTransfer.getData('text/post-id');
                        const p = posts.find(x => x.id === id);
                        if (p) moverParaData(p, dia);
                        setArrastando(null); setHoverDia(null);
                      }}
                      onClick={() => {
                        // Default: criar com 09:00 no dia clicado
                        onCriarComData(dia.hour(9).minute(0).second(0).toISOString());
                      }}
                      title={ehMesAtual ? `Clique pra criar post em ${dia.format('DD/MM')}` : ''}
                      style={{
                        height: 92,
                        background: isHover ? '#EFF3FF'
                          : ehHoje ? '#FFF8E8'
                          : !ehMesAtual ? '#FAFBFF'
                          : ehFimSemana ? '#FAFAFB'
                          : '#fff',
                        border: ehHoje ? '2px solid #0930D0' : '1px solid #E5E7EB',
                        borderRadius: 4,
                        padding: 4,
                        cursor: 'pointer',
                        opacity: !ehMesAtual ? 0.55 : 1,
                        display: 'flex', flexDirection: 'column',
                        overflow: 'hidden',
                      }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between',
                        alignItems: 'center', marginBottom: 4 }}>
                        <Text style={{ fontSize: 12, fontWeight: ehHoje ? 700 : 500,
                          color: ehHoje ? '#0930D0' : ehMesAtual ? '#222' : '#999' }}>
                          {dia.date()}
                        </Text>
                        {restante > 0 && (
                          <Text type="secondary" style={{ fontSize: 9 }}>
                            +{restante}
                          </Text>
                        )}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2,
                        flex: 1, minHeight: 0 }}>
                        {visiveis.map(p => (
                          <ChipPostCalendario key={p.id} post={p}
                            arrastando={arrastando === p.id}
                            onDragStart={() => setArrastando(p.id)}
                            onClick={() => onEditar(p)} />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Chip compacto de post no calendário ───────────────────────────────
function ChipPostCalendario({ post, arrastando, onDragStart, onClick }: {
  post: Post; arrastando: boolean;
  onDragStart: () => void;
  onClick: () => void;
}) {
  const st = STATUS[post.status];
  const primeiraRede = (post.redes || [])[0];
  const hora = post.publicar_em ? dayjs(post.publicar_em).format('HH:mm') : '';
  return (
    <div draggable
      onDragStart={e => {
        e.dataTransfer.setData('text/post-id', post.id);
        e.stopPropagation();
        onDragStart();
      }}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      title={`${st.label}${hora ? ' · ' + hora : ''}\n${post.titulo}`}
      style={{
        display: 'flex', alignItems: 'center', gap: 3,
        background: st.bg,
        borderLeft: `3px solid ${st.cor}`,
        borderRadius: 3,
        padding: '2px 4px',
        fontSize: 10,
        cursor: arrastando ? 'grabbing' : 'grab',
        opacity: arrastando ? 0.5 : 1,
        overflow: 'hidden',
      }}>
      {primeiraRede && <span style={{ fontSize: 10 }}>{REDES[primeiraRede]?.emoji}</span>}
      {hora && (
        <span style={{ color: st.cor, fontWeight: 600, fontSize: 9, flexShrink: 0 }}>
          {hora}
        </span>
      )}
      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis',
        whiteSpace: 'nowrap', color: '#222' }}>
        {post.titulo}
      </span>
    </div>
  );
}

// ─── Drawer "Gerar com IA" ──────────────────────────────────────────────
// Pede prompt + estilo (+ referência opcional a contrato/ART/atividade) e chama
// /agentes/exemplos action='mkt_rascunhar_post'. Mostra preview e permite usar
// ou regenerar antes de aplicar nos campos do ModalPost.
const ESTILOS_IA = [
  { value: 'linkedin_tecnico',      label: 'LinkedIn técnico (case detalhado, 150-280 palavras)' },
  { value: 'linkedin_institucional',label: 'LinkedIn institucional (marca, 100-180 palavras)' },
  { value: 'instagram_casual',      label: 'Instagram casual (curto, 60-120 palavras)' },
  { value: 'noticia_evento',        label: 'Notícia / Evento (factual, 80-160 palavras)' },
];
const TIPOS_REF = [
  { value: '',          label: 'Sem referência (só prompt)' },
  { value: 'contrato',  label: '📄 Contrato' },
  { value: 'art',       label: '📝 ART' },
  { value: 'atividade', label: '⚙️ Atividade técnica' },
];

interface RascunhoIa {
  titulo: string;
  conteudo: string;
  hashtags: string;
  redes_sugeridas: string[];
}

interface UsoIa {
  texto: { chamadas: number; custo_usd: number; limite_usd: number; pct_usado: number };
  imagem: { chamadas: number; custo_usd: number; limite_usd: number; pct_usado: number };
}

function BarraUsoIa({ uso }: { uso: UsoIa | null }) {
  if (!uso) return null;
  const fmtUsd = (n: number) => `US$ ${n.toFixed(2)}`;
  const corBarra = (pct: number) => pct >= 90 ? '#B91626' : pct >= 70 ? '#B07500' : '#0a7f4a';
  return (
    <Card size="small" style={{ marginBottom: 12, background: '#FAFBFF' }} bodyStyle={{ padding: 10 }}>
      <Text type="secondary" style={{ fontSize: 10, display: 'block', marginBottom: 6 }}>
        💰 Cota diária da IA (global Hydrodata)
      </Text>
      <div style={{ marginBottom: 6 }}>
        <Flex justify="space-between" style={{ fontSize: 10 }}>
          <Text>📝 Texto</Text>
          <Text>{fmtUsd(uso.texto.custo_usd)} / {fmtUsd(uso.texto.limite_usd)} · {uso.texto.chamadas} chamada(s)</Text>
        </Flex>
        <Progress percent={uso.texto.pct_usado} size="small" showInfo={false}
          strokeColor={corBarra(uso.texto.pct_usado)} />
      </div>
      <div>
        <Flex justify="space-between" style={{ fontSize: 10 }}>
          <Text>🎨 Imagem</Text>
          <Text>{fmtUsd(uso.imagem.custo_usd)} / {fmtUsd(uso.imagem.limite_usd)} · {uso.imagem.chamadas} chamada(s)</Text>
        </Flex>
        <Progress percent={uso.imagem.pct_usado} size="small" showInfo={false}
          strokeColor={corBarra(uso.imagem.pct_usado)} />
      </div>
    </Card>
  );
}

function DrawerGerarIa({ open, onClose, onUsar, onUsarImagem }: {
  open: boolean;
  onClose: () => void;
  onUsar: (r: RascunhoIa) => void;
  onUsarImagem: (m: Midia) => void;
}) {
  const { message } = AntApp.useApp();
  const [uso, setUso] = useState<UsoIa | null>(null);

  // Texto
  const [prompt, setPrompt] = useState('');
  const [estilo, setEstilo] = useState('linkedin_tecnico');
  const [tipoRef, setTipoRef] = useState<'' | 'contrato' | 'art' | 'atividade'>('');
  const [refId, setRefId] = useState<string | undefined>(undefined);
  const [opcoesRef, setOpcoesRef] = useState<{ value: string; label: string }[]>([]);
  const [carregandoOpcoes, setCarregandoOpcoes] = useState(false);
  const [gerando, setGerando] = useState(false);
  const [resultado, setResultado] = useState<RascunhoIa | null>(null);

  // Imagem
  const [tipoImagem, setTipoImagem] = useState<'template' | 'livre'>('template');
  const [tplTitulo, setTplTitulo] = useState('');
  const [tplSubtitulo, setTplSubtitulo] = useState('');
  const [tplTags, setTplTags] = useState('');
  const [promptImg, setPromptImg] = useState('');
  const [gerandoImg, setGerandoImg] = useState(false);
  const [resultadoImg, setResultadoImg] = useState<Midia | null>(null);

  async function carregarUso() {
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/agentes/exemplos`,
        { action: 'mkt_ia_uso_atual' },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      if (d?.texto && d?.imagem) setUso(d);
    } catch { /* silencioso — barra é só informativa */ }
  }
  useEffect(() => { if (open) carregarUso(); }, [open]);

  // Carrega opções quando muda o tipo de referência
  useEffect(() => {
    if (!tipoRef) { setOpcoesRef([]); setRefId(undefined); return; }
    (async () => {
      setCarregandoOpcoes(true);
      try {
        const token = await getToken();
        const action = tipoRef === 'contrato' ? 'op_listar_contratos'
          : tipoRef === 'art' ? 'op_listar_arts'
          : 'op_listar_atividades';
        const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
          { action },
          { headers: { Authorization: token } });
        const d = r.data.body ? JSON.parse(r.data.body) : r.data;
        const arr = Array.isArray(d) ? d : (d.items || []);
        const opcoes = arr.map((x: any) => {
          if (tipoRef === 'contrato') {
            return { value: x.id, label: `${x.numero || ''} — ${x.titulo || x.cliente_nome || x.id}`.slice(0, 100) };
          }
          if (tipoRef === 'art') {
            return { value: x.id, label: `ART ${x.numero || ''} — ${x.tipo_art || ''} ${x.rt_nome ? '(' + x.rt_nome + ')' : ''}`.slice(0, 100) };
          }
          return { value: x.id, label: `${x.titulo || ''} ${x.status ? '· ' + x.status : ''}`.slice(0, 100) };
        });
        setOpcoesRef(opcoes);
      } catch (e: any) {
        message.error('Erro ao listar ' + tipoRef + ': ' + (e.response?.data?.erro || e.message));
        setOpcoesRef([]);
      } finally { setCarregandoOpcoes(false); }
    })();
  }, [tipoRef, message]);

  async function gerarTexto() {
    if (!prompt.trim() && (!tipoRef || !refId)) {
      message.warning('Forneça um prompt OU selecione uma referência.');
      return;
    }
    setGerando(true);
    setResultado(null);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/agentes/exemplos`,
        {
          action: 'mkt_rascunhar_post',
          prompt: prompt.trim(),
          estilo,
          referencia: tipoRef && refId ? { tipo: tipoRef, id: refId } : null,
        },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      if (d?.erro) {
        message.error(d.erro);
        return;
      }
      setResultado({
        titulo: d.titulo || '',
        conteudo: d.conteudo || '',
        hashtags: d.hashtags || '',
        redes_sugeridas: d.redes_sugeridas || [],
      });
      carregarUso();
    } catch (e: any) {
      const status = e.response?.status;
      const msg = e.response?.data?.erro || e.message;
      if (status === 429) message.error('Limite diário esgotado: ' + msg);
      else message.error('Erro: ' + msg);
    } finally { setGerando(false); }
  }

  async function gerarImagem() {
    // Valida conforme o modo
    let payload: any;
    if (tipoImagem === 'template') {
      if (!tplTitulo.trim() && !tplSubtitulo.trim() && !tplTags.trim()) {
        message.warning('Preencha ao menos um campo do template.');
        return;
      }
      payload = {
        action: 'mkt_gerar_imagem',
        provider: 'imejis',
        fields: {
          titulo: tplTitulo.trim() || undefined,
          subtitulo: tplSubtitulo.trim() || undefined,
          tags: tplTags.trim() || undefined,
        },
      };
    } else {
      if (!promptImg.trim()) {
        message.warning('Descreva a imagem que deseja gerar.');
        return;
      }
      payload = {
        action: 'mkt_gerar_imagem',
        provider: 'stability',
        prompt: promptImg.trim(),
      };
    }
    setGerandoImg(true);
    setResultadoImg(null);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/agentes/exemplos`, payload,
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      if (d?.erro) {
        message.error(d.erro);
        return;
      }
      setResultadoImg({
        s3_key: d.s3_key,
        url_publica: d.url_publica,
        tipo: 'imagem',
        mime: d.mime || 'image/png',
        tamanho_bytes: d.tamanho_bytes || 0,
        nome_original: d.nome_original || 'rascunho-ia.png',
      });
      carregarUso();
    } catch (e: any) {
      const status = e.response?.status;
      const msg = e.response?.data?.erro || e.message;
      if (status === 429) message.error('Limite diário esgotado: ' + msg);
      else message.error('Erro: ' + msg);
    } finally { setGerandoImg(false); }
  }

  function fechar() {
    setResultado(null);
    setResultadoImg(null);
    onClose();
  }

  return (
    <Drawer
      open={open}
      onClose={fechar}
      title={<span><ThunderboltOutlined style={{ color: '#7C3AED' }} /> Gerar com IA</span>}
      width={540}
      destroyOnClose
    >
      <BarraUsoIa uso={uso} />

      <Tabs
        size="small"
        items={[
          {
            key: 'texto',
            label: '📝 Texto do post',
            children: (
              <>
                <Form layout="vertical" size="small">
                  <Form.Item label="Estilo do post" required>
                    <Select value={estilo} onChange={setEstilo} options={ESTILOS_IA} />
                  </Form.Item>

                  <Form.Item label="Referência do sistema (opcional)">
                    <Select value={tipoRef}
                      onChange={(v) => { setTipoRef(v); setRefId(undefined); }}
                      options={TIPOS_REF} />
                  </Form.Item>

                  {tipoRef && (
                    <Form.Item label={`Selecione ${tipoRef === 'contrato' ? 'o contrato' : tipoRef === 'art' ? 'a ART' : 'a atividade'}`} required>
                      <Select value={refId} onChange={setRefId}
                        loading={carregandoOpcoes} options={opcoesRef}
                        showSearch optionFilterProp="label"
                        placeholder={carregandoOpcoes ? 'Carregando…' : 'Buscar…'}
                        notFoundContent={carregandoOpcoes ? <Spin size="small" /> : 'Nada encontrado'} />
                    </Form.Item>
                  )}

                  <Form.Item label="Sobre o que é o post? (opcional se houver referência)">
                    <Input.TextArea value={prompt} onChange={e => setPrompt(e.target.value)} rows={4}
                      placeholder="Ex.: Anuncie a conclusão do estudo hidrológico do contrato XYZ, destacando o uso de dados de campo e a entrega no prazo." />
                  </Form.Item>

                  <Button type="primary" block icon={<ThunderboltOutlined />}
                    loading={gerando} onClick={gerarTexto}
                    style={{ background: '#7C3AED', borderColor: '#7C3AED' }}>
                    {resultado ? 'Gerar outro' : 'Gerar rascunho'}
                  </Button>
                </Form>

                {resultado && (
                  <div style={{ marginTop: 16 }}>
                    <Alert type="success" showIcon message="Rascunho gerado"
                      description="Revise antes de aplicar — você pode editar tudo depois no modal."
                      style={{ marginBottom: 12 }} />
                    <Card size="small" style={{ marginBottom: 10 }} title="Conteúdo">
                      <div style={{ fontSize: 12, whiteSpace: 'pre-wrap', color: '#222' }}>
                        {resultado.conteudo}
                      </div>
                    </Card>
                    {resultado.hashtags && (
                      <Card size="small" style={{ marginBottom: 10 }} title="Hashtags">
                        <Text style={{ fontSize: 11, color: '#0930D0' }}>{resultado.hashtags}</Text>
                      </Card>
                    )}
                    {resultado.redes_sugeridas?.length > 0 && (
                      <Card size="small" style={{ marginBottom: 10 }} title="Redes sugeridas">
                        <Space wrap>
                          {resultado.redes_sugeridas.map(r => (
                            <Tag key={r} color={REDES[r]?.cor || 'default'}>
                              {REDES[r]?.emoji} {REDES[r]?.label || r}
                            </Tag>
                          ))}
                        </Space>
                      </Card>
                    )}
                    <Flex gap={8} justify="flex-end">
                      <Button onClick={() => setResultado(null)}>Descartar</Button>
                      <Button type="primary" icon={<CheckCircleOutlined />}
                        onClick={() => onUsar(resultado)}
                        style={{ background: '#0a7f4a', borderColor: '#0a7f4a' }}>
                        Usar este rascunho
                      </Button>
                    </Flex>
                  </div>
                )}
              </>
            ),
          },
          {
            key: 'imagem',
            label: '🎨 Imagem',
            children: (
              <>
                <Segmented
                  block size="small" value={tipoImagem}
                  onChange={(v) => setTipoImagem(v as 'template' | 'livre')}
                  options={[
                    { label: '📐 Template BHND', value: 'template' },
                    { label: '🎨 Ilustração livre', value: 'livre' },
                  ]}
                  style={{ marginBottom: 12 }}
                />

                {tipoImagem === 'template' ? (
                  <Form layout="vertical" size="small">
                    <Alert
                      type="info" showIcon
                      message="Card BHND (1080×1920 — Instagram Story)"
                      description="Preenche os 3 textos do template. Logo Hydrodata e layout vêm prontos."
                      style={{ marginBottom: 10, fontSize: 11 }}
                    />
                    <Form.Item label="Título principal (Heading One)">
                      <Input.TextArea value={tplTitulo} onChange={e => setTplTitulo(e.target.value)}
                        rows={2} placeholder="Ex.: Estudo Hidrológico — Rio Pelotas concluído" />
                    </Form.Item>
                    <Form.Item label="Subtítulo (Heading Two)">
                      <Input.TextArea value={tplSubtitulo} onChange={e => setTplSubtitulo(e.target.value)}
                        rows={2} placeholder="Ex.: Diagnóstico de cheias para construtora XYZ" />
                    </Form.Item>
                    <Form.Item label="Hashtags / tags (Text with #tags)">
                      <Input value={tplTags} onChange={e => setTplTags(e.target.value)}
                        placeholder="Ex.: #hidrologia #seguranca-hidrica #engenharia" />
                    </Form.Item>

                    <Button type="primary" block icon={<ThunderboltOutlined />}
                      loading={gerandoImg} onClick={gerarImagem}
                      style={{ background: '#7C3AED', borderColor: '#7C3AED' }}>
                      {resultadoImg ? 'Gerar outra' : 'Gerar card BHND'}
                    </Button>
                  </Form>
                ) : (
                  <Form layout="vertical" size="small">
                    <Form.Item label="Descreva a imagem que deseja gerar" required
                      help="Seja específico: estilo (ilustração/foto), elementos, cores, ambiente. Modelo: Stable Image Core (1:1).">
                      <Input.TextArea value={promptImg} onChange={e => setPromptImg(e.target.value)} rows={5}
                        placeholder="Ex.: Ilustração editorial em estilo flat, vista aérea de uma bacia hidrográfica com sensores IoT, paleta azul/cinza, luz natural, sem texto." />
                    </Form.Item>

                    <Button type="primary" block icon={<ThunderboltOutlined />}
                      loading={gerandoImg} onClick={gerarImagem}
                      style={{ background: '#7C3AED', borderColor: '#7C3AED' }}>
                      {resultadoImg ? 'Gerar outra' : 'Gerar ilustração'}
                    </Button>
                  </Form>
                )}

                {resultadoImg && (
                  <div style={{ marginTop: 16 }}>
                    <Alert type="success" showIcon message="Imagem gerada"
                      description="A imagem já está salva. Click em 'Usar' pra anexar como mídia do post."
                      style={{ marginBottom: 12 }} />
                    <div style={{ textAlign: 'center', marginBottom: 12 }}>
                      <Image src={resultadoImg.url_publica}
                        style={{ maxWidth: '100%', borderRadius: 6 }} />
                    </div>
                    <Flex gap={8} justify="flex-end">
                      <Button onClick={() => setResultadoImg(null)}>Descartar</Button>
                      <Button type="primary" icon={<CheckCircleOutlined />}
                        onClick={() => onUsarImagem(resultadoImg)}
                        style={{ background: '#0a7f4a', borderColor: '#0a7f4a' }}>
                        Usar como mídia
                      </Button>
                    </Flex>
                  </div>
                )}
              </>
            ),
          },
        ]}
      />
    </Drawer>
  );
}

// ─── Modal de criar/editar post ─────────────────────────────────────────
function ModalPost({ post, publicarEmInicial, meuEmail, ehAdmin, usuarios, onClose, onSalvo }: {
  post: Post | null; publicarEmInicial?: string | null;
  meuEmail: string; ehAdmin?: boolean;
  usuarios: { email: string; nome: string }[];
  onClose: () => void; onSalvo: () => void;
}) {
  const { message, modal } = AntApp.useApp();
  const novo = !post;
  const [titulo, setTitulo] = useState(post?.titulo || '');
  const [conteudo, setConteudo] = useState(post?.conteudo || '');
  const [redes, setRedes] = useState<string[]>(post?.redes || []);
  const [hashtags, setHashtags] = useState(post?.hashtags || '');
  const [aprovador, setAprovador] = useState(post?.aprovador_email || '');
  const [publicarEm, setPublicarEm] = useState<string>(post?.publicar_em || publicarEmInicial || '');
  const [midias, setMidias] = useState<Midia[]>(post?.midias || []);
  const [midiasUpload, setMidiasUpload] = useState<MidiaUpload[]>([]);  // novas a enviar
  const [salvando, setSalvando] = useState(false);
  const [iaAberta, setIaAberta] = useState(false);

  function aplicarRascunhoIa(r: { titulo: string; conteudo: string; hashtags: string; redes_sugeridas: string[] }) {
    if (r.titulo && !titulo) setTitulo(r.titulo);
    setConteudo(r.conteudo || '');
    if (r.hashtags) setHashtags(r.hashtags);
    if (Array.isArray(r.redes_sugeridas) && r.redes_sugeridas.length > 0) {
      const validas = r.redes_sugeridas.filter(x => REDES[x]);
      if (validas.length > 0) setRedes(validas);
    }
    setIaAberta(false);
  }

  function aplicarImagemIa(m: Midia) {
    setMidias(prev => [...prev, { ...m, ordem: prev.length }]);
    setIaAberta(false);
  }

  const ehAprovador = !!post && post.aprovador_email?.toLowerCase() === meuEmail.toLowerCase();
  const ehAutor = !!post && post.criado_por.toLowerCase() === meuEmail.toLowerCase();

  async function lerArquivo(file: File): Promise<string> {
    return new Promise((res, rej) => {
      const reader = new FileReader();
      reader.onload = () => res(String(reader.result));
      reader.onerror = () => rej(reader.error);
      reader.readAsDataURL(file);
    });
  }

  async function adicionarArquivos(files: FileList | null) {
    if (!files) return;
    const novos: MidiaUpload[] = [];
    for (const f of Array.from(files)) {
      if (f.size > 10 * 1024 * 1024) {
        message.warning(`${f.name} excede 10 MB — ignorado`);
        continue;
      }
      novos.push({ data: await lerArquivo(f), nome: f.name, mime: f.type || 'application/octet-stream' });
    }
    setMidiasUpload(prev => [...prev, ...novos]);
  }

  function removerMidiaSalva(s3Key: string) {
    setMidias(prev => prev.filter(m => m.s3_key !== s3Key));
  }
  function removerMidiaUpload(idx: number) {
    setMidiasUpload(prev => prev.filter((_, i) => i !== idx));
  }

  async function salvar() {
    if (!titulo.trim()) { message.warning('Título obrigatório'); return; }
    setSalvando(true);
    try {
      const token = await getToken();
      const payload: any = {
        action: novo ? 'mkt_post_criar' : 'mkt_post_atualizar',
        id: post?.id,
        titulo: titulo.trim(),
        conteudo: conteudo || null,
        redes,
        hashtags: hashtags || null,
        aprovador_email: aprovador || null,
        publicar_em: publicarEm || null,
      };
      if (midiasUpload.length > 0) payload.midias_upload = midiasUpload;
      // Mídias prontas (já no S3 — ex: imagem gerada por IA) — usado em criação
      if (novo && midias.length > 0) {
        payload.midias_prontas = midias;
      }
      // Se editou e removeu alguma mídia salva
      if (!novo && midias.length !== (post?.midias?.length || 0)) {
        payload.midias_replace = midias;
      }
      const r = await axios.post(`${API_URL}/atividades`, payload, { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      if (d.erro) { message.error(d.erro); return; }
      message.success(novo ? 'Post criado.' : 'Post atualizado.');
      onSalvo();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.erro || e.message));
    } finally { setSalvando(false); }
  }

  async function acao(action: string, extra: any = {}) {
    setSalvando(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/atividades`,
        { action, id: post!.id, ...extra },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      if (d.erro) { message.error(d.erro); return; }
      message.success('OK');
      onSalvo();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.erro || e.message));
    } finally { setSalvando(false); }
  }

  function rejeitarComMotivo() {
    let motivo = '';
    modal.confirm({
      title: 'Rejeitar post — informe o motivo',
      content: (
        <Input.TextArea rows={3} onChange={e => { motivo = e.target.value; }}
          placeholder="Ex.: foto fora do padrão da marca, texto longo demais, etc." />
      ),
      okText: 'Rejeitar', okType: 'danger', cancelText: 'Voltar',
      onOk: () => {
        if (!motivo.trim()) {
          message.error('Motivo obrigatório');
          return Promise.reject();
        }
        return acao('mkt_post_rejeitar', { motivo: motivo.trim() });
      },
    });
  }

  function excluir() {
    modal.confirm({
      title: 'Excluir post?',
      content: 'Esta ação não pode ser desfeita.',
      okText: 'Excluir', okType: 'danger', cancelText: 'Voltar',
      onOk: () => acao('mkt_post_excluir'),
    });
  }

  return (
    <Modal open onCancel={onClose} width={820} destroyOnClose
      title={
        <Flex justify="space-between" align="center" wrap gap={8} style={{ paddingRight: 30 }}>
          <span>{novo ? '📱 Novo post' : `📱 Editar post — ${STATUS[post!.status].label}`}</span>
          <Button size="small" type="default"
            icon={<ThunderboltOutlined />}
            onClick={() => setIaAberta(true)}
            style={{ color: '#7C3AED', borderColor: '#7C3AED' }}>
            Gerar com IA
          </Button>
        </Flex>
      }
      footer={
        <Flex justify="space-between" wrap="wrap" gap={6}>
          <Space>
            {!novo && (ehAutor || ehAprovador || ehAdmin) && (
              <Button danger icon={<DeleteOutlined />} onClick={excluir}>Excluir</Button>
            )}
          </Space>
          <Space wrap>
            {/* Ações de workflow — só pra posts já existentes */}
            {!novo && post!.status === 'rascunho' && (ehAutor || ehAdmin) && (
              <Button icon={<SendOutlined />}
                onClick={() => acao('mkt_post_enviar_revisao')}
                style={{ color: '#B07500', borderColor: '#B07500' }}>
                Enviar pra revisão
              </Button>
            )}
            {!novo && post!.status === 'em_revisao' && (ehAprovador || ehAdmin) && (
              <>
                <Button danger icon={<CloseCircleOutlined />} onClick={rejeitarComMotivo}>
                  Rejeitar
                </Button>
                <Button type="primary" icon={<CheckCircleOutlined />}
                  onClick={() => acao('mkt_post_aprovar')}
                  style={{ background: '#0a7f4a', borderColor: '#0a7f4a' }}>
                  Aprovar
                </Button>
              </>
            )}
            {!novo && post!.status === 'aprovado' && publicarEm && (
              <Button type="primary" icon={<ClockCircleOutlined />}
                onClick={() => acao('mkt_post_agendar', { publicar_em: publicarEm })}>
                Agendar
              </Button>
            )}
            {!novo && ['aprovado', 'agendado'].includes(post!.status) && (
              <Button type="primary" onClick={() => acao('mkt_post_publicar')}
                style={{ background: '#7C3AED', borderColor: '#7C3AED' }}>
                Marcar como publicado
              </Button>
            )}
            <Button onClick={onClose}>Fechar</Button>
            <Button type="primary" loading={salvando} onClick={salvar}>
              {novo ? 'Criar' : 'Salvar'}
            </Button>
          </Space>
        </Flex>
      }>
      <Form layout="vertical" size="small">
        <Form.Item label="Título" required>
          <Input value={titulo} onChange={e => setTitulo(e.target.value)} autoFocus
            placeholder="Ex.: Lançamento do Estudo Hidrológico XYZ" />
        </Form.Item>
        <Form.Item label="Conteúdo / legenda">
          <Input.TextArea value={conteudo} onChange={e => setConteudo(e.target.value)}
            rows={5} placeholder="Texto que vai como legenda do post" />
        </Form.Item>
        <Flex gap={10} wrap="wrap">
          <Form.Item label="Redes" style={{ flex: 1, minWidth: 200 }}>
            <Select mode="multiple" value={redes} onChange={setRedes}
              placeholder="Onde vai publicar"
              options={Object.entries(REDES).map(([k, v]) => ({
                label: `${v.emoji} ${v.label}`, value: k,
              }))} />
          </Form.Item>
          <Form.Item label="Hashtags" style={{ flex: 1, minWidth: 200 }}>
            <Input value={hashtags} onChange={e => setHashtags(e.target.value)}
              placeholder="#hidrologia #engenharia ..." />
          </Form.Item>
        </Flex>
        <Flex gap={10} wrap="wrap">
          <Form.Item label="Aprovador (responsável pela revisão)" style={{ flex: 1, minWidth: 220 }}>
            <Select value={aprovador || undefined} onChange={(v) => setAprovador(v || '')}
              placeholder="— sem aprovador —"
              showSearch allowClear
              options={usuarios.map(u => ({ label: `${u.nome} (${u.email})`, value: u.email }))} />
          </Form.Item>
          <Form.Item label="Data de publicação (opcional)" style={{ flex: 1, minWidth: 200 }}>
            <DatePicker showTime format="DD/MM/YYYY HH:mm"
              value={publicarEm ? dayjs(publicarEm) : null}
              onChange={(d) => setPublicarEm(d ? d.toISOString() : '')}
              style={{ width: '100%' }} />
          </Form.Item>
        </Flex>

        {/* Mídias */}
        <Form.Item label="Mídias (imagens, vídeos, PDF, Word, Excel, PowerPoint, TXT — máx 10 MB cada)">
          <Flex gap={8} wrap style={{ marginBottom: 8 }}>
            {midias.map(m => (
              <div key={m.s3_key} style={{ position: 'relative', width: 90, height: 90 }}>
                {m.tipo === 'imagem' ? (
                  <Image src={m.url_publica} width={90} height={90}
                    style={{ objectFit: 'cover', borderRadius: 4 }} />
                ) : m.tipo === 'video' ? (
                  <a href={m.url_publica} target="_blank" rel="noopener noreferrer"
                    title="Abrir vídeo"
                    style={{ width: 90, height: 90, background: '#000',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#fff', fontSize: 24, borderRadius: 4 }}>▶</a>
                ) : (
                  <a href={m.url_publica} target="_blank" rel="noopener noreferrer"
                    title={m.nome_original || 'Baixar arquivo'}
                    style={{ width: 90, height: 90, background: '#FAFBFF',
                      border: '1px solid #D0D5DD', borderRadius: 4,
                      display: 'flex', flexDirection: 'column', alignItems: 'center',
                      justifyContent: 'center', color: '#020D7F', fontSize: 9, padding: 4,
                      textAlign: 'center', textDecoration: 'none', overflow: 'hidden' }}>
                    <span style={{ fontSize: 28, lineHeight: 1 }}>{iconePorMime(m.mime)}</span>
                    <span style={{ marginTop: 4, wordBreak: 'break-all',
                      display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                      overflow: 'hidden' }}>
                      {m.nome_original || (m.s3_key.split('/').pop() || 'arquivo')}
                    </span>
                  </a>
                )}
                <Button size="small" danger type="text"
                  onClick={() => removerMidiaSalva(m.s3_key)}
                  style={{ position: 'absolute', top: -8, right: -8, background: '#fff',
                    border: '1px solid #ddd', borderRadius: '50%', width: 22, height: 22,
                    padding: 0, fontSize: 12 }}>×</Button>
              </div>
            ))}
            {midiasUpload.map((m, i) => {
              const tipoUp = m.mime.startsWith('image/') ? 'imagem'
                : m.mime.startsWith('video/') ? 'video' : 'documento';
              return (
                <div key={i} style={{ position: 'relative', width: 90, height: 90 }}>
                  {tipoUp === 'imagem' ? (
                    <img src={m.data} alt={m.nome} style={{ width: 90, height: 90,
                      objectFit: 'cover', borderRadius: 4, opacity: 0.7 }} />
                  ) : tipoUp === 'video' ? (
                    <div style={{ width: 90, height: 90, background: '#000', opacity: 0.7,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#fff', fontSize: 24, borderRadius: 4 }}>▶</div>
                  ) : (
                    <div title={m.nome} style={{ width: 90, height: 90, background: '#FAFBFF',
                      border: '1px solid #D0D5DD', borderRadius: 4, opacity: 0.85,
                      display: 'flex', flexDirection: 'column', alignItems: 'center',
                      justifyContent: 'center', color: '#020D7F', fontSize: 9, padding: 4,
                      textAlign: 'center', overflow: 'hidden' }}>
                      <span style={{ fontSize: 28, lineHeight: 1 }}>{iconePorMime(m.mime)}</span>
                      <span style={{ marginTop: 4, wordBreak: 'break-all',
                        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                        overflow: 'hidden' }}>{m.nome}</span>
                    </div>
                  )}
                  <span style={{ position: 'absolute', top: 4, left: 4, fontSize: 9,
                    background: 'rgba(9,48,208,0.85)', color: '#fff', padding: '0 4px',
                    borderRadius: 2 }}>NOVA</span>
                  <Button size="small" danger type="text"
                    onClick={() => removerMidiaUpload(i)}
                    style={{ position: 'absolute', top: -8, right: -8, background: '#fff',
                      border: '1px solid #ddd', borderRadius: '50%', width: 22, height: 22,
                      padding: 0, fontSize: 12 }}>×</Button>
                </div>
              );
            })}
          </Flex>
          <label style={{ display: 'inline-block', cursor: 'pointer',
            padding: '6px 14px', border: '1px dashed #0930D0', borderRadius: 5,
            color: '#0930D0', fontSize: 12 }}>
            <PictureOutlined /> Adicionar arquivos
            <input type="file" multiple
              accept="image/*,video/*,application/pdf,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/plain,text/csv"
              style={{ display: 'none' }}
              onChange={(e) => adicionarArquivos(e.target.files)} />
          </label>
        </Form.Item>

        {post?.status === 'rejeitado' && post?.rejeitado_motivo && (
          <Card size="small" style={{ background: '#FDECEC', borderColor: '#B91626' }}>
            <Text strong style={{ color: '#B91626', fontSize: 11 }}>❌ Motivo da rejeição:</Text>
            <div style={{ fontSize: 12, color: '#444' }}>{post.rejeitado_motivo}</div>
          </Card>
        )}
      </Form>

      {/* Thread de comentários — só em posts já criados */}
      {post?.id && (
        <ComentariosMkt postId={post.id} meuEmail={meuEmail} usuarios={usuarios} />
      )}

      <DrawerGerarIa
        open={iaAberta}
        onClose={() => setIaAberta(false)}
        onUsar={aplicarRascunhoIa}
        onUsarImagem={aplicarImagemIa}
      />
    </Modal>
  );
}

// ─── Histórico do post (eventos automáticos + comentários humanos) ──────
// Backend: action mkt_post_historico devolve tudo unificado, ordenado por criado_em.
// Eventos viram linhas system (cinza), comentários renderizam como antes.
const ICONES_EVENTO: Record<string, string> = {
  criado: '📝', editado: '✏️', em_revisao: '📋',
  aprovado: '✅', rejeitado: '❌', agendado: '⏰',
  publicado: '📣', arquivado: '📦',
};

function ComentariosMkt({ postId, meuEmail, usuarios }: {
  postId: string; meuEmail: string; usuarios: { email: string; nome: string }[];
}) {
  const { message } = AntApp.useApp();
  const [historico, setHistorico] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [texto, setTexto] = useState('');
  const [respondendoA, setRespondendoA] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  async function carregar() {
    setLoading(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/atividades`,
        { action: 'mkt_post_historico', post_id: postId },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setHistorico(Array.isArray(d) ? d : []);
    } catch {} finally { setLoading(false); }
  }
  useEffect(() => { carregar(); /* eslint-disable-next-line */ }, [postId]);

  // Separa pra a árvore de respostas (só comentários têm parent_id)
  const comentarios = historico.filter(h => h.kind === 'comentario');

  function extrairMencoes(t: string): string[] {
    const re = /@([a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;
    const set = new Set<string>();
    let m;
    while ((m = re.exec(t)) !== null) set.add(m[1].toLowerCase());
    return Array.from(set);
  }

  async function enviar() {
    const t = texto.trim();
    if (!t) return;
    setEnviando(true);
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/atividades`,
        { action: 'mkt_comentario_criar', post_id: postId, parent_id: respondendoA,
          texto: t, mencoes: extrairMencoes(t) },
        { headers: { Authorization: token } });
      setTexto(''); setRespondendoA(null);
      await carregar();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.erro || e.message));
    } finally { setEnviando(false); }
  }

  async function excluir(id: string) {
    if (!window.confirm('Excluir comentário?')) return;
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/atividades`,
        { action: 'mkt_comentario_excluir', id },
        { headers: { Authorization: token } });
      await carregar();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.erro || e.message));
    }
  }

  // Linhas raiz da timeline: eventos (sem hierarquia) + comentários sem parent.
  // Como o backend já devolve ordenado por criado_em ASC, basta filtrar.
  const raiz = historico.filter(h => h.kind === 'evento' || (h.kind === 'comentario' && !h.parent_id));
  const respostasDe = (id: string) => comentarios.filter(c => c.parent_id === id);

  function renderEvento(e: any) {
    return (
      <div key={`ev-${e.id}`} style={{ display: 'flex', alignItems: 'center', gap: 8,
        padding: '6px 8px', marginTop: 6, background: '#FAFBFF',
        borderLeft: '3px solid #D0D5DD', borderRadius: 3,
        color: '#6A7092', fontSize: 11 }}>
        <span style={{ fontSize: 14 }}>{ICONES_EVENTO[e.subtipo] || '•'}</span>
        <span style={{ flex: 1, minWidth: 0, wordBreak: 'break-word' }}>
          <strong style={{ color: '#020D7F' }}>
            {(e.autor_email || 'sistema').split('@')[0]}
          </strong>
          {' · '}{e.descricao}
        </span>
        <span style={{ fontSize: 10, flexShrink: 0 }}>
          {new Date(e.criado_em).toLocaleString('pt-BR')}
        </span>
      </div>
    );
  }

  function renderTextoComMencoes(t: string) {
    const re = /@([a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;
    const partes: any[] = [];
    let last = 0; let m; let idx = 0;
    while ((m = re.exec(t)) !== null) {
      if (m.index > last) partes.push(t.slice(last, m.index));
      partes.push(<strong key={`m${idx++}`} style={{ color: '#0930D0' }}>@{m[1]}</strong>);
      last = m.index + m[0].length;
    }
    if (last < t.length) partes.push(t.slice(last));
    return partes.length === 0 ? t : partes;
  }

  function renderComentario(c: any, prof = 0): any {
    const respostas = respostasDe(c.id);
    const ehMeu = c.autor_email.toLowerCase() === meuEmail.toLowerCase();
    const ini = (c.autor_nome || c.autor_email).split(/[\s@]/)
      .filter(Boolean).slice(0, 2).map((s: string) => s[0]).join('').toUpperCase();
    return (
      <div key={c.id} style={{ marginLeft: prof * 24, marginTop: 8 }}>
        <Flex gap={8} align="flex-start">
          <div style={{ width: 28, height: 28, borderRadius: 14, background: '#020D7F',
            color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 11, fontWeight: 600, flexShrink: 0 }}>{ini}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Flex gap={6} align="center" wrap>
              <Text strong style={{ fontSize: 12, color: '#020D7F' }}>
                {c.autor_nome || c.autor_email}
              </Text>
              <Text type="secondary" style={{ fontSize: 10 }}>
                {new Date(c.criado_em).toLocaleString('pt-BR')}
                {c.editado_em && ' · editado'}
              </Text>
            </Flex>
            <div style={{ fontSize: 12, color: '#222', whiteSpace: 'pre-wrap', marginTop: 2 }}>
              {renderTextoComMencoes(c.texto)}
            </div>
            {Array.isArray(c.mencoes) && c.mencoes.length > 0 && (
              <Space size={4} wrap style={{ marginTop: 3 }}>
                {c.mencoes.map((e: string) => (
                  <Tag key={e} color="blue" style={{ fontSize: 9, padding: '0 5px', lineHeight: '16px' }}>
                    🔔 {e}
                  </Tag>
                ))}
              </Space>
            )}
            <Space size={8} style={{ marginTop: 3 }}>
              {prof < 2 && (
                <Button size="small" type="link" style={{ fontSize: 10, padding: 0, height: 'auto' }}
                  onClick={() => setRespondendoA(c.id)}>↩ Responder</Button>
              )}
              {ehMeu && (
                <Button size="small" type="link" danger
                  style={{ fontSize: 10, padding: 0, height: 'auto' }}
                  onClick={() => excluir(c.id)}>Excluir</Button>
              )}
            </Space>
          </div>
        </Flex>
        {respostas.map(r => renderComentario(r, prof + 1))}
      </div>
    );
  }

  return (
    <div style={{ marginTop: 14, paddingTop: 12, borderTop: '0.5px solid #e0e0e0' }}>
      <Text type="secondary" style={{ fontSize: 11 }}>
        💬 Histórico {comentarios.length > 0 && `(${comentarios.length} coment.)`}
      </Text>

      {loading ? <Spin size="small" /> : raiz.length === 0 ? (
        <Text type="secondary" italic style={{ fontSize: 11, display: 'block',
          textAlign: 'center', padding: 8 }}>
          Sem histórico ainda. Use @email pra mencionar alguém.
        </Text>
      ) : (
        <div style={{ maxHeight: 320, overflowY: 'auto', paddingRight: 6 }}>
          {raiz.map(item => item.kind === 'evento'
            ? renderEvento(item)
            : renderComentario(item))}
        </div>
      )}

      <div style={{ marginTop: 10, background: '#FAFBFF', padding: 8, borderRadius: 5 }}>
        {respondendoA && (
          <Flex justify="space-between" align="center" style={{ marginBottom: 6 }}>
            <Text style={{ fontSize: 10, color: '#0930D0' }}>↩ Respondendo</Text>
            <Button size="small" type="link" style={{ fontSize: 10, padding: 0, height: 'auto' }}
              onClick={() => setRespondendoA(null)}>cancelar</Button>
          </Flex>
        )}
        <Mentions
          value={texto}
          onChange={setTexto}
          rows={2}
          autoSize={{ minRows: 2, maxRows: 4 }}
          placeholder="Comente aqui. Use @ para mencionar alguém."
          options={usuarios.map(u => ({ value: u.email, label: `${u.nome} (${u.email})` }))}
        />
        <Flex justify="flex-end" style={{ marginTop: 6 }}>
          <Button type="primary" size="small" loading={enviando}
            disabled={!texto.trim()} onClick={enviar}>
            {respondendoA ? 'Responder' : 'Comentar'}
          </Button>
        </Flex>
      </div>
    </div>
  );
}
